import Avatar from 'ext-ui/components/Avatar/Avatar'
import React from 'react'
import {
  firstPresentMetadataValue,
  getPointDisplayLabel,
  isOpaqueReportingKey,
} from 'src/charting/reporting/reportingChartData'
import { ConfigContext } from 'src/context/ConfigContext/ConfigContextBase'
import { ExtendCurrency } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import {
  SpendChartPoint,
  SpendChartView,
} from 'src/queries/reporting/reporting.models'
import { useQueryUser } from 'src/queries/users.queries'

import * as style from '../Reporting.style'
import { formatMoney, getInitials, RankedSpendRow } from './reportingPanelUtils'

const getPersonId = (point: SpendChartPoint): string | undefined =>
  firstPresentMetadataValue(point, ['userId', 'personId', 'cardholderId']) ??
  point.key ??
  point.seriesKey ??
  undefined

const getPersonServerLabel = (point: SpendChartPoint): string =>
  getPointDisplayLabel(point, '', [
    'personName',
    'cardholderName',
    'userName',
    'displayName',
    'fullName',
    'email',
  ])

const TopSpenderRow: React.FC<{
  row: RankedSpendRow
  currency: ExtendCurrency
}> = ({ row, currency }) => {
  const translate = useTranslate()
  const { locale } = React.useContext(ConfigContext)
  const needsHydration = isOpaqueReportingKey(row.label)
  const userId = needsHydration ? getPersonId(row.point) : undefined
  const userQuery = useQueryUser({
    userId,
    enabled: userId !== undefined,
  })

  const label = needsHydration
    ? userQuery.isLoading
      ? translate('Loading person')
      : (userQuery.data?.fullNameOrEmail ?? translate('Unknown person'))
    : row.label
  const initials = getInitials(label)

  return (
    <li css={style.topSpenderRow} data-testid="top-spender-row">
      <span css={style.topSpenderRank}>{row.rank}</span>
      <span css={style.topSpenderAvatar} aria-hidden="true">
        <Avatar
          variant="outlined"
          size={32}
          fallback={
            <span css={style.topSpenderAvatarInitials}>{initials}</span>
          }
        />
      </span>
      <span css={style.topSpenderName}>{label}</span>
      <span css={style.topSpenderAmount}>
        {formatMoney(row.amountCents, currency, locale)}
      </span>
    </li>
  )
}

const toTopSpenderRows = (chart: SpendChartView): RankedSpendRow[] =>
  [...chart.points]
    .sort((a, b) => b.spendCents - a.spendCents)
    .slice(0, 5)
    .map((point, index) => {
      const label = getPersonServerLabel(point)

      return {
        id: getPersonId(point) ?? `${point.label}-${index}`,
        rank: index + 1,
        label,
        amountCents: point.spendCents,
        point,
      }
    })

const TopSpendersList: React.FC<{
  chart: SpendChartView
  currency: ExtendCurrency
}> = ({ chart, currency }) => {
  const translate = useTranslate()
  const rows = toTopSpenderRows(chart)

  return (
    <ol css={style.topSpendersList} aria-label={translate('Top spenders')}>
      {rows.map((row) => (
        <TopSpenderRow key={row.id} row={row} currency={currency} />
      ))}
    </ol>
  )
}

export default TopSpendersList
